import React from 'react';
import {
  Box, Chip, Paper, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { Visibility as EyeIcon } from '@mui/icons-material';

const DataViewStep = ({ config, itemContext = {}, response = {}, onChange, disabled }) => {
  const theme = useTheme();
  const sampleData = itemContext.sample_data;
  const schema = itemContext.schema;
  const taskDescription = itemContext.task_description;

  return (
    <Stack spacing={2.5}>
      {taskDescription && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.info.main, 0.04),
            borderColor: alpha(theme.palette.info.main, 0.3),
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
            Task context
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {taskDescription}
          </Typography>
        </Paper>
      )}

      {schema && (
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Schema / structure
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {Object.entries(schema).map(([col, dtype]) => (
              <Chip
                key={col}
                label={`${col}: ${dtype}`}
                size="small"
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
              />
            ))}
          </Box>
        </Box>
      )}

      {sampleData && (
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Sample data
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0, p: 2,
              bgcolor: '#0d0d0d',
              color: '#abb2bf',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 240,
              whiteSpace: 'pre',
              border: '1px solid #333',
            }}
          >
            {typeof sampleData === 'string' ? sampleData : JSON.stringify(sampleData, null, 2)}
          </Box>
        </Box>
      )}

      <Box>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <EyeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={700}>
            What do you observe?
          </Typography>
        </Stack>
        {config.hints?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
            {config.hints.map((hint, i) => (
              <Chip
                key={i}
                label={hint}
                size="small"
                variant="outlined"
                color="default"
                sx={{ fontSize: '0.78rem', color: 'text.secondary' }}
              />
            ))}
          </Box>
        )}
        <TextField
          value={response.text || ''}
          onChange={(e) => onChange({ ...response, text: e.target.value })}
          placeholder="Describe what you see — size, types, patterns, anomalies, edge cases…"
          multiline
          minRows={4}
          fullWidth
          disabled={disabled}
          variant="outlined"
          size="small"
        />
      </Box>
    </Stack>
  );
};

export default DataViewStep;
